import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import {
  AutoAwesome as DraftIcon,
  ThumbUp as ApprovedIcon,
  Send as PostedIcon,
  Lock as ClosedIcon,
  Block as VoidIcon,
} from '@mui/icons-material';
import { ledgerAPI } from '../../../services/api';
import { LedgerPicker, useLedgerOptions } from '../LedgerPickers';

// ---------------------------------------------------------------------------
// Shared Budgeting UI kit - mirrors receivables/ArShared.js,
// payables/ApShared.js, expenses/ExpensesShared.js and income/IncomeShared.js
// so Stewardship reads as one product with the rest of the ledger.
// ---------------------------------------------------------------------------

export const getErrorMessage = (error, fallback = 'Operation failed') => {
  const data = error?.response?.data;
  const message = data?.message || fallback;
  // The Laravel gateway forwards the ledger's per-field validation detail in
  // `errors` (see LedgerGatewayController) - surface it so a bare
  // "validation failed" message still tells the user which field to fix.
  const details = data?.errors;
  if (!details) return message;
  const flat = Array.isArray(details)
    ? details.map((d) => (typeof d === 'string' ? d : JSON.stringify(d)))
    : Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
  return flat.length ? `${message}: ${flat.join('; ')}` : message;
};

export const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

export const unwrapList = (response) => {
  const payload = unwrap(response);
  return Array.isArray(payload) ? payload : payload.items ?? [];
};

export const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime())
    ? String(d)
    : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Budget line statuses (verified against BudgetService): DRAFT ->
// SUBMITTED -> APPROVED -> LOCKED. Transfers add POSTED/REJECTED.
const STATUS_CFG = {
  DRAFT: { label: 'Draft', color: 'default', Icon: DraftIcon },
  SUBMITTED: { label: 'Submitted', color: 'info', Icon: PostedIcon },
  APPROVED: { label: 'Approved', color: 'success', Icon: ApprovedIcon },
  POSTED: { label: 'Posted', color: 'info', Icon: PostedIcon },
  LOCKED: { label: 'Locked', color: 'default', Icon: ClosedIcon },
  ACTIVE: { label: 'Active', color: 'success', Icon: ApprovedIcon },
  CLOSED: { label: 'Closed', color: 'default', Icon: ClosedIcon },
  REJECTED: { label: 'Rejected', color: 'error', Icon: VoidIcon },
  VOID: { label: 'Void', color: 'error', Icon: VoidIcon },
};

export const BudgetStatusChip = ({ status }) => {
  const st = String(status || 'DRAFT').toUpperCase();
  const cfg = STATUS_CFG[st] || { label: status || 'Draft', color: 'default', Icon: DraftIcon };
  const { Icon } = cfg;
  return <Chip icon={<Icon fontSize="small" />} label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
};

export const StatTile = ({ label, value, color }) => (
  <Box sx={{ flex: '1 1 140px', textAlign: 'center', py: 1.5, px: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight={700} color={color} sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
);

export const StatStrip = ({ tiles = [] }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    divider={<Divider orientation="vertical" flexItem />}
    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}
  >
    {tiles.map((t) => (
      <StatTile key={t.label} {...t} />
    ))}
  </Stack>
);

export const budgetIdOf = (b) => b?.budget_id ?? b?.id;

// Budget lines are per (account x year x period) rows - label them by their
// account, falling back to the legacy header-style fields if present.
export const budgetLabelOf = (b) => {
  if (!b) return '';
  if (b.account) return `${b.account.account_code} - ${b.account.account_name}`;
  return b?.budget_number || b?.budget_name || b?.name || (budgetIdOf(b) ? `Budget #${budgetIdOf(b)}` : '');
};

// Budget picker scoped to the 'budgeting' gateway section, so stewards only
// need ledger/budgeting read (unlike a coa/gl-scoped lookup).
export const BudgetPicker = (props) => {
  const { options, loading } = useLedgerOptions('budgeting-budgets', () =>
    ledgerAPI.getBudgets({ limit: 100 }).then((res) =>
      unwrapList(res).map((b) => ({ value: budgetIdOf(b), label: `${budgetLabelOf(b)}` }))
    )
  );
  return <LedgerPicker label="Budget" {...props} options={options} loading={loading} />;
};
